import React from "react";
import { cn } from "../../lib/utils";
import {
  TestRunQualitativeEvaluationSummary,
  CriterionBreakdown,
} from "../../model_gen/evaluation";
import { SCORE_META } from "./TestRunCriteriaBreakdown";

interface Props {
  testRunResults: TestRunQualitativeEvaluationSummary | null;
  selectedCriteria: string | null;
  selectedScores: number[];
  onCriteriaSelect: (criteria: string | null) => void;
  onScoreClick: (score: number) => void;
  onScoreFilterClear: () => void;
}

const TestRunScoreFilter: React.FC<Props> = ({
  testRunResults,
  selectedCriteria,
  selectedScores,
  onCriteriaSelect,
  onScoreClick,
  onScoreFilterClear,
}) => {
  const breakdowns = testRunResults?.criteria_breakdowns || [];
  const overallScores = testRunResults?.request_scores || [];
  if (breakdowns.length === 0 && overallScores.length === 0) return null;

  const activeBd: CriterionBreakdown | null =
    breakdowns.find((b) => b.criteria_name === selectedCriteria) || null;
  const activeScores = activeBd?.score_distribution || overallScores;
  const totalScored = activeScores.reduce((s, i) => s + i.count, 0);

  return (
    <div className="mb-6 space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => onCriteriaSelect(null)}
          className={cn(
            "px-3 py-1.5 text-xs rounded-full border transition-colors font-gc-reg",
            !selectedCriteria
              ? "bg-maitai-green/10 text-maitai-green border-maitai-green/30 font-gc-med"
              : "border-border text-muted-foreground hover:text-foreground"
          )}
        >
          All
        </button>
        {breakdowns.map((bd) => (
          <button
            key={bd.criteria_name}
            onClick={() =>
              onCriteriaSelect(
                selectedCriteria === bd.criteria_name ? null : bd.criteria_name
              )
            }
            className={cn(
              "px-3 py-1.5 text-xs rounded-full border transition-colors font-gc-reg",
              selectedCriteria === bd.criteria_name
                ? "bg-maitai-green/10 text-maitai-green border-maitai-green/30 font-gc-med"
                : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {bd.criteria_name}
            {bd.avg_numeric_score != null && (
              <span className="ml-1.5 text-[10px] opacity-70">
                {bd.avg_numeric_score.toFixed(1)}
              </span>
            )}
          </button>
        ))}
      </div>

      {totalScored > 0 && (
        <div className="space-y-3">
          <div className="flex rounded-md overflow-hidden h-6">
            {SCORE_META.map(({ score, label, color }) => {
              const entry = activeScores.find((i) => i.score === score);
              const count = entry?.count || 0;
              if (count === 0) return null;
              const pct = (count / totalScored) * 100;
              const isActive =
                selectedScores.length === 0 || selectedScores.includes(score);
              return (
                <div
                  key={score}
                  onClick={() => onScoreClick(score)}
                  title={`${label}: ${count.toLocaleString()} (${pct.toFixed(1)}%)`}
                  className="h-full transition-all duration-300 cursor-pointer"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: color,
                    opacity: isActive ? 1 : 0.3,
                    boxShadow: selectedScores.includes(score)
                      ? "inset 0 0 0 2px rgba(255,255,255,0.6)"
                      : "none",
                  }}
                />
              );
            })}
          </div>

          <div className="grid grid-cols-5 gap-2">
            {SCORE_META.map(({ score, label, color }) => {
              const entry = activeScores.find((i) => i.score === score);
              const count = entry?.count || 0;
              const pct = totalScored > 0 ? (count / totalScored) * 100 : 0;
              const isActive =
                selectedScores.length === 0 || selectedScores.includes(score);
              return (
                <button
                  key={score}
                  onClick={() => onScoreClick(score)}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-left transition-all",
                    selectedScores.includes(score)
                      ? "border-foreground/30 bg-muted/60"
                      : "border-border hover:bg-muted/40",
                    !isActive && "opacity-40"
                  )}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-gc-med text-foreground truncate">
                      {label}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-gc-reg">
                      {count} · {pct.toFixed(1)}%
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedScores.length > 0 && (
            <button
              onClick={onScoreFilterClear}
              className="text-xs text-blue-500 hover:text-blue-400 font-gc-med"
            >
              Clear score filter
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TestRunScoreFilter;
